import { deviceMetadata, generateId, isBrowser } from "./environment";
import * as logger from "./logger";
import type { SessionStamp } from "./session";
import { SessionManager } from "./session";
import { transport } from "./transport";
import type { CustomFields, BusinessAnalyticsConfig, ResolvedConfig, TrackedEvent } from "./types";

const DEFAULTS = {
	sessionTimeoutMs: 30 * 60 * 1000,
	batchSize: 20,
	flushIntervalMs: 5_000,
	maxQueueSize: 500,
	maxRetries: 3,
	retryBaseDelayMs: 1_000,
	maxRetryDelayMs: 30_000,
	debug: false,
};

/** A tracked event before a session is available to stamp it with. */
type PendingEvent = Omit<TrackedEvent, "sessionId">;

let config: ResolvedConfig | null = null;
let session: SessionManager | null = null;
let buffer: PendingEvent[] = [];
/** Set by identify(). Not resolved via a callback: the app knows the moment login happens, the SDK does not. */
let currentUserId: string | null = null;

function resolveConfig(input: BusinessAnalyticsConfig): ResolvedConfig {
	return {
		apiKey: input.apiKey,
		endpoint: input.endpoint,
		utilityName: input.utilityName,
		appVersion: input.appVersion ?? null,
		sessionTimeoutMs: input.sessionTimeoutMs ?? DEFAULTS.sessionTimeoutMs,
		batchSize: input.batchSize ?? DEFAULTS.batchSize,
		flushIntervalMs: input.flushIntervalMs ?? DEFAULTS.flushIntervalMs,
		maxQueueSize: input.maxQueueSize ?? DEFAULTS.maxQueueSize,
		maxRetries: input.maxRetries ?? DEFAULTS.maxRetries,
		retryBaseDelayMs: input.retryBaseDelayMs ?? DEFAULTS.retryBaseDelayMs,
		maxRetryDelayMs: input.maxRetryDelayMs ?? DEFAULTS.maxRetryDelayMs,
		debug: input.debug ?? DEFAULTS.debug,
	};
}

/**
 * TypeScript erases these types, so a plain-JS caller or a @ts-ignore can still hand us a
 * nested object. Drop what the contract cannot carry rather than sending something the
 * backend will reject or silently mangle.
 */
function sanitizeCustomFields(fields: CustomFields | undefined): CustomFields {
	if (!fields) return {};

	const clean: CustomFields = {};
	for (const [key, value] of Object.entries(fields)) {
		if (typeof value === "string" || typeof value === "boolean") {
			clean[key] = value;
		} else if (typeof value === "number" && Number.isFinite(value)) {
			clean[key] = value;
		} else {
			logger.warn(`dropped custom field "${key}" — only finite numbers, strings and booleans are supported`, value);
		}
	}
	return clean;
}

function bufferEvent(event: PendingEvent): void {
	const cap = config?.maxQueueSize ?? DEFAULTS.maxQueueSize;
	if (buffer.length >= cap) {
		buffer.shift();
		logger.warn(`pre-init buffer is full (${cap}); dropped the oldest event`);
	}
	buffer.push(event);
}

/** Close the session this call replaced, then register the new one. Order matters upstream. */
function announce(stamp: SessionStamp): void {
	if (stamp.endedSession) {
		transport.endSession(stamp.endedSession.sessionId, stamp.endedSession.endedAt);
	}
	if (stamp.isNew) {
		transport.startSession({ sessionId: stamp.sessionId, startedAt: stamp.startedAt }, deviceMetadata());
	}
}

/** Buffered events all predate init() within one page load, so they share one session. */
function flushBuffer(): void {
	if (buffer.length === 0 || !session || !isBrowser()) return;

	const pending = buffer;
	buffer = [];

	const stamp = session.stamp();
	announce(stamp);
	for (const event of pending) {
		transport.enqueue({ ...event, sessionId: stamp.sessionId });
	}
	logger.log(`flushed ${pending.length} buffered event(s)`);
}

/**
 * Singleton, enforced by the type system: a frozen object literal with no exported class
 * and no constructor. Not a React context or hook — the SDK is framework-agnostic and
 * imports nothing from React.
 */
export const BusinessAnalytics = Object.freeze({
	init(input: BusinessAnalyticsConfig): void {
		const resolved = resolveConfig(input);
		logger.setDebug(resolved.debug);

		if (config) {
			logger.warn("init() called more than once; the latest configuration wins");
		}
		if (!resolved.endpoint || !resolved.apiKey) {
			logger.warn("delivery is disabled: endpoint or apiKey is empty");
		}

		config = resolved;
		session = new SessionManager({ timeoutMs: resolved.sessionTimeoutMs });
		transport.init(resolved);
		flushBuffer();
	},

	track(eventName: string, flowName: string, customFields?: CustomFields): void {
		if (typeof eventName !== "string" || !eventName || typeof flowName !== "string" || !flowName) {
			logger.warn("track() needs a non-empty eventName and flowName", { eventName, flowName });
			return;
		}

		const pending: PendingEvent = {
			insertId: generateId(),
			eventName,
			flowName,
			customFields: sanitizeCustomFields(customFields),
			userId: currentUserId,
			timestamp: new Date().toISOString(),
		};

		// Before init() there is no session to stamp with, and outside a browser there are no
		// session semantics at all — buffer in both cases rather than inventing a session.
		if (!config || !session || !isBrowser()) {
			bufferEvent(pending);
			return;
		}

		const stamp = session.stamp();
		announce(stamp);
		transport.enqueue({ ...pending, sessionId: stamp.sessionId });
	},

	/**
	 * Call once the signed-in user is known, and again with null at logout. From here on
	 * events carry the id, and the backend attaches it to the session itself on the first
	 * event that has one — so there is nothing to send now.
	 *
	 * A logout does not erase a userId already recorded on a session: that history stays
	 * accurate. It only stops attaching an id to events from here on.
	 */
	identify(userId: string | null): void {
		currentUserId = userId;
	},
});
